import { captureMessage } from '@sentry/react';

import { redactDomainsFromText } from '@/lib/sentry-domain-redaction';

import type { supabase } from '@/integrations/supabase/client';

// =============================================================================
// Subscribe-status machine for useRealtimeChannel
//
// The classification + retry-scheduling logic lives here (separate from the hook
// itself) so both stay readable. Everything is driven through a SubscribeContext
// the hook builds per channel lifetime; nothing here closes over hook state.
// =============================================================================

/**
 * Join failures on an OPEN socket before the "this channel keeps failing"
 * Sentry warning is eligible. Crossing it does NOT stop retries — retries
 * continue at the capped backoff.
 *
 * The count is a duration in disguise, and deliberately so. `SUBSCRIBED` resets
 * it, so reaching N means N failures with no successful join in between; and the
 * backoff schedule is deterministic, so the cumulative wait at retry 7 is at
 * least 1+2+4+8+16+32+60 = 123s. Two minutes is past the recovery window of the
 * two failure modes that dominate in production — a socket closed 1006 across
 * sleep/wake or a network transition, and a join attempted while the access
 * token is mid-refresh — both of which heal themselves and are not worth an
 * event.
 *
 * Only failures that are evidence against the CHANNEL count: a join that the
 * server rejected or that timed out on an open socket, or an auth prime that
 * failed before any socket was involved. A join that times out on a closed or
 * reconnecting socket, or an error the socket layer fans out to every channel
 * (see TRANSPORT_ERROR_PATTERN), describes the socket, not the channel — and a
 * sleeping laptop is not idle: macOS dark wakes run the pending retry timer
 * with no network, so a tab left overnight accrues a dozen failed retries
 * before anyone looks at it. Counting those tripped the gate on the first join
 * after wake, which raced the socket reconnect; a warning at that instant
 * described the wake, not the channel. Counting attempts rather than
 * wall-clock keeps the bound immune to NTP steps and background timer
 * throttling.
 */
export const MAX_RETRIES_BEFORE_WARN = 7;
const BASE_BACKOFF_MS = 1000;
/**
 * Ceiling for exponential backoff. After the 5th retry the raw backoff would
 * be 32s, then 64s, then 128s — the cap clamps every attempt past that to
 * `MAX_BACKOFF_MS`. Picked to balance fast recovery (sub-minute) against load
 * on a genuinely-down backend.
 */
const MAX_BACKOFF_MS = 60_000;
const RETRYABLE_SUBSCRIBE_STATUSES = new Set(['CHANNEL_ERROR', 'TIMED_OUT', 'CLOSED']);

/**
 * Attempts before an authorization denial is treated as terminal. The first
 * denial can be the unprimed-socket race (join raced `setAuth`, auth.uid()
 * null); by the third attempt the socket token has settled, so a denial there
 * means realtime.messages RLS genuinely rejects these claims — retrying with
 * the same JWT can never succeed and only produces a server-side error storm
 * (one parked tab was logging ~1,440 Unauthorized/day). Recovery happens on
 * TOKEN_REFRESHED (new claims) or an effect re-run (org/channel change), not
 * on a timer.
 */
export const MAX_UNAUTHORIZED_ATTEMPTS = 3;

/**
 * The realtime server rejects a join denied by realtime.messages RLS with
 * "You do not have permissions to read from this Channel topic: ..." (write
 * denials use "write to"). realtime-js surfaces the reason on the subscribe
 * callback's Error — in some versions JSON.stringified — so this matches on
 * the stable phrase, not the exact shape. Expired/invalid-JWT errors use
 * different wording and MUST stay retryable (they self-heal on refresh).
 */
const AUTHORIZATION_DENIAL_PATTERN =
  /you do not have permissions to (?:read from|write to) this channel/i;

export function isChannelAuthorizationDenial(error: Error | undefined): boolean {
  return error !== undefined && AUTHORIZATION_DENIAL_PATTERN.test(error.message);
}

/**
 * Retries of continuous expired-token failure before the warning fires anyway.
 * A lapsed token is a recovering state — the retry loop re-primes auth from the
 * current session on every attempt, so the join succeeds once the session
 * refreshes — but only while a refresh is plausibly still coming. A session
 * whose refresh is wedged (revoked refresh token, a refresh that never lands)
 * would otherwise retry silently for its whole life, which is the symptom RC-2
 * describes with the alarm removed. ~5 minutes of cumulative backoff.
 */
export const EXPIRED_JWT_WARN_RETRIES = 10;

/**
 * The realtime server rejects a join carrying a lapsed access token with
 * "InvalidJWTToken: Token has expired <n> seconds ago". Deliberately narrow —
 * expiry only. A structurally invalid token produces different wording, never
 * self-heals, and warns on the normal schedule.
 *
 * This classifies the WARNING only. Expired-JWT errors stay on the elastic
 * retry path: parking them would cost up to a full token TTL of dead realtime,
 * the ordering hazard described at the bottom of `handleSubscribeStatus`.
 * Background: RC-2 in
 * `docs/plans/2026-07-04-sharing-realtime-enterprise-hardening-plan.md`.
 */
const EXPIRED_JWT_PATTERN = /token has expired/i;

function isExpiredJwtError(error: Error | undefined): boolean {
  return error !== undefined && EXPIRED_JWT_PATTERN.test(error.message);
}

/**
 * Errors the socket layer fans out to EVERY channel when the transport fails,
 * as opposed to a reply to this channel's own join. They describe the socket,
 * so they must not count as evidence against the channel.
 *
 * Shapes, by source. `@supabase/phoenix@0.4.5` socket.js `heartbeatTimeout`
 * calls `triggerChanError(new Error("heartbeat timeout"))` BEFORE it tears the
 * WebSocket down, so `isConnected()` still reads true when that error reaches
 * the subscribe callback — the socket probe alone cannot catch it. A close
 * event on the connection reaches the callback through `@supabase/realtime-js`
 * `normalizeChannelError` as "socket closed: <code>" (a CloseEvent is the only
 * input that carries a numeric code), and a missing reason as "channel error:
 * connection lost".
 *
 * "channel error: transport failure" is deliberately NOT in the pattern:
 * normalizeChannelError produces it for ANY object without a numeric code,
 * which is both the transport `Event` of a connection error and the payload of
 * a server `phx_error` — a crashed channel process on a healthy socket, exactly
 * what the warning exists to surface. The preserved `cause` tells them apart.
 */
const TRANSPORT_ERROR_PATTERN =
  /^(?:heartbeat timeout|socket closed: |channel error: connection lost)/i;
const AMBIGUOUS_TRANSPORT_MESSAGE = 'channel error: transport failure';

function isTransportError(error: Error | undefined): boolean {
  if (error === undefined) {
    return false;
  }
  if (TRANSPORT_ERROR_PATTERN.test(error.message)) {
    return true;
  }
  // The app's TS lib target (ES2021) predates `Error.cause`; realtime-js sets
  // it regardless, to the raw reason it normalised.
  const { cause } = error as { cause?: unknown };
  return (
    error.message === AMBIGUOUS_TRANSPORT_MESSAGE &&
    typeof Event !== 'undefined' &&
    cause instanceof Event
  );
}

/**
 * Where a retryable failure came from. `join` is the subscribe callback of a
 * live channel; `auth-prime` is `primeRealtimeAuthForPrivateChannel` rejecting
 * before a channel exists, which involves no socket at all.
 */
export type FailureOrigin = 'join' | 'auth-prime';

/**
 * Canonical 8-4-4-4-12 UUID layout, on purpose rather than "36 hex-or-dash
 * characters": the loose run also matches the separator dash that precedes a
 * UUID in a `<family>-<uuid>` topic, swallowing it and leaving the UUID's last
 * character behind — `share-access-<uuid>` redacted to `share-access[id]d`, so
 * Sentry grouped one issue per user instead of one per channel family, the
 * exact fragmentation the pass exists to prevent.
 *
 * `UUID_SEGMENT` is the same layout anchored to a whole topic segment. Keeping
 * the two in lockstep is the point: a looser segment test would surface an
 * identifier the message had redacted.
 */
// eslint-disable-next-line unicorn/better-regex -- expanded form avoids security/detect-unsafe-regex
const UUID_ANYWHERE = /[\da-f]{8}-[\da-f]{4}-[\da-f]{4}-[\da-f]{4}-[\da-f]{12}/gi;
// eslint-disable-next-line unicorn/better-regex -- expanded form avoids security/detect-unsafe-regex
const UUID_SEGMENT = /^[\da-f]{8}-[\da-f]{4}-[\da-f]{4}-[\da-f]{4}-[\da-f]{12}$/i;

/**
 * Channel names reach Sentry as the warning message AND as the raw `channel`
 * tag, and tags bypass the `beforeSend` free-text scrubbers. Some channels
 * embed identifiers derived from mail data — a thread-key channel carries an
 * RFC 5322 Message-ID (`...thread-<id>@<mailbox host>`), which the email
 * scrub catches — so the name must be fully redacted here, at the source.
 * Order: emails/domains, then UUIDs, then any residual hex run of 12+
 * (thread keys, short hashes) — the UUID pass must precede the hex pass so a
 * dashed UUID collapses to one [id] instead of several. The identifier
 * passes also stop per-entity names fragmenting Sentry grouping into one
 * issue per entity.
 */
function sanitizeChannelName(name: string): string {
  return redactDomainsFromText(name)
    .replaceAll(UUID_ANYWHERE, '[id]')
    .replaceAll(/[\da-f]{12,}/gi, '[id]');
}

/**
 * Every UUID segment of the topic, in order.
 *
 * Plural because a single "the scope id" is not well defined across our topic
 * shapes: `org:<org>:presence` and `tasks-sync:<org>` carry one id,
 * `comment-room:<org>:<entityType>:<entityId>` carries two, and
 * `minrep:<taskId>:presence` carries one that is a TASK, not an org. Returning
 * the first match would silently report an org for some topics and an entity
 * for others.
 *
 * In order, they line up 1:1 with the `[id]` placeholders in the message this
 * rides on, so a reader maps them back by position against the topic shape.
 *
 * Segment-anchored, so a thread-key topic (which carries a Message-ID, not a
 * UUID) yields nothing rather than putting mail-derived text into an extra no
 * `beforeSend` scrubber is guaranteed to reach.
 */
function extractScopeIds(channelName: string): string[] {
  return channelName.split(':').filter((segment) => UUID_SEGMENT.test(segment));
}

/**
 * One-shot-per-call-site Sentry warning for a channel parked on authorization
 * denial. Shared by useRealtimeChannel and the presence hooks so the event is
 * queryable under a single message.
 */
export function captureChannelAuthorizationDenied(channelName: string): void {
  const sanitizedName = sanitizeChannelName(channelName);
  captureMessage(`[Real-time] Channel authorization denied, parking: ${sanitizedName}`, {
    level: 'warning',
    tags: { channel: sanitizedName },
    // The message and tag collapse every id so one problem groups into ONE
    // issue; that grouping is why the ids have to come back somewhere. A denial
    // on an org-scoped topic is `user_belongs_to_organization` returning false,
    // and the only way to tell a genuine non-membership from an identity
    // mismatch is to check the org against the member list of the user the
    // event is attributed to. Opaque internal UUIDs, never mail-derived: the
    // domain and hex-run passes above have already removed anything that could
    // be, and these are positionally aligned with the message's `[id]` slots.
    extra: { scope_ids: extractScopeIds(channelName) },
  });
}

export type Channel = ReturnType<typeof supabase.channel>;

export interface SubscribeContext {
  channel: Channel;
  channelName: string;
  generation: number;
  generationRef: React.RefObject<number>;
  retryCountRef: React.RefObject<number>;
  warnedRef: React.RefObject<boolean>;
  // Whether the hook's current subscription has had a successful SUBSCRIBED.
  // Kept across retries and the hook being switched off and on, reset when the
  // subscription's identity changes; the first SUBSCRIBED sets it true (initial
  // join) and fires onFirstSubscribed, every later one is a recovery and fires
  // onResubscribed.
  hasSubscribedOnceRef: React.RefObject<boolean>;
  fireOnResubscribed: () => void;
  fireOnFirstSubscribed: () => void;
  // Per-channel set of channels whose `removeChannel` ack is still pending — and,
  // more broadly, channels we've torn down whose lifetime is over. Any status
  // from a channel in this set is ignored. Per-channel (not a single boolean) so
  // that a callback on a DIFFERENT channel — e.g. a fresh channel created by a
  // new effect lifetime after StrictMode double-mount or a rapid dependency
  // change — is never incorrectly suppressed while a prior channel's tear-down
  // is still in flight.
  tearingDownChannelsRef: React.RefObject<WeakSet<Channel>>;
  // Consecutive authorization denials for the current channel lifetime. Reset
  // on SUBSCRIBED and on any retryable failure that is NOT a denial (mixed
  // failure modes mean the backend is unstable, not that RLS rejects us).
  unauthorizedCountRef: React.RefObject<number>;
  // Retryable failures that count as evidence against the channel, for the
  // current channel lifetime (see MAX_RETRIES_BEFORE_WARN). Reset on
  // SUBSCRIBED. Gates the persistent-failure warning only; `retryCountRef`
  // keeps driving the backoff and the `retryCount` tag.
  channelFailureCountRef: React.RefObject<number>;
  // Whether the shared realtime socket is open right now.
  isSocketConnected: () => boolean;
  // Park the channel: the hook stops retrying and re-subscribes only on
  // TOKEN_REFRESHED or an effect re-run. Called after MAX_UNAUTHORIZED_ATTEMPTS
  // consecutive denials.
  markParked: () => void;
  setRetryTimer: (timer: ReturnType<typeof setTimeout>) => void;
  createAndSubscribe: () => void;
  removeChannel: (channel: Channel) => void;
}

export type RetryContext = Omit<
  SubscribeContext,
  | 'channel'
  | 'removeChannel'
  | 'tearingDownChannelsRef'
  | 'hasSubscribedOnceRef'
  | 'fireOnResubscribed'
  | 'fireOnFirstSubscribed'
  | 'unauthorizedCountRef'
  | 'markParked'
>;

/**
 * Whether a scheduled retry should also emit the persistent-failure warning.
 *
 * Every negative answer here deliberately leaves `warnedRef` unset, so a channel
 * that is STILL failing once the suppressing condition lifts warns on its next
 * retry rather than losing the warning for the whole failure cycle.
 *
 * None of this changes retry behaviour — the caller retries regardless. It
 * decides only whether an outage is worth a Sentry event yet.
 */
function shouldWarnPersistentFailure(ctx: RetryContext, error: Error | undefined): boolean {
  const retry = ctx.retryCountRef.current;
  if (ctx.channelFailureCountRef.current < MAX_RETRIES_BEFORE_WARN || ctx.warnedRef.current) {
    return false;
  }
  // An offline client fails every channel and recovers on reconnect, so the
  // warning would describe the network, not the channel. `onLine === false` is
  // the trustworthy direction of that flag.
  if (globalThis.navigator?.onLine === false) {
    return false;
  }
  // A stale channel in a backgrounded tab is invisible to the user, and the tab
  // is also where throttled timers stretch the backoff furthest from its
  // nominal schedule. If it is still failing when the tab is looked at again,
  // the next retry warns.
  if (globalThis.document?.visibilityState === 'hidden') {
    return false;
  }
  // Recovering, not broken — but not forever; see EXPIRED_JWT_WARN_RETRIES.
  return !isExpiredJwtError(error) || retry >= EXPIRED_JWT_WARN_RETRIES;
}

export function scheduleSubscribeRetry(
  ctx: RetryContext,
  status?: string,
  error?: Error,
  origin: FailureOrigin = 'join',
): void {
  const retry = ctx.retryCountRef.current;

  // Evidence against the CHANNEL, as opposed to the socket under it: a join
  // that failed or timed out on an open socket, or an auth prime that failed
  // before any socket was involved. A transport fan-out, or a join attempted on
  // a closed socket, still backs off — it just does not bring the warning
  // closer, and cannot be the failure that trips it (see
  // MAX_RETRIES_BEFORE_WARN).
  const isChannelEvidence =
    origin === 'auth-prime' || (ctx.isSocketConnected() && !isTransportError(error));

  // One-shot Sentry warning once the outage has outlived plausible self-repair.
  // The retry budget is intentionally elastic — crossing the threshold means
  // "this has been failing for a while," not "give up." Gated on the count
  // BEFORE this failure is added, like `retryCountRef` below, so the warning
  // fires on the failure after the threshold — the cumulative wait
  // MAX_RETRIES_BEFORE_WARN documents. See
  // docs/plans/2026-05-27-fix-realtime-channel-retry-exhaustion-no-recovery-plan.md
  if (isChannelEvidence && shouldWarnPersistentFailure(ctx, error)) {
    const sanitizedName = sanitizeChannelName(ctx.channelName);
    captureMessage(`[Real-time] Persistent channel failure: ${sanitizedName}`, {
      level: 'warning',
      tags: {
        channel: sanitizedName,
        retryCount: retry,
        subscribeStatus: status ?? 'unknown',
        subscribeError:
          error === undefined ? 'none' : sanitizeChannelName(error.message).slice(0, 200),
      },
    });
    ctx.warnedRef.current = true;
  }

  if (isChannelEvidence) {
    ctx.channelFailureCountRef.current += 1;
  }
  const backoff = Math.min(BASE_BACKOFF_MS * 2 ** retry, MAX_BACKOFF_MS);
  ctx.retryCountRef.current += 1;
  ctx.setRetryTimer(setTimeout(ctx.createAndSubscribe, backoff));
}

/**
 * Authorization denial (realtime.messages RLS rejected the join): a small
 * FIXED budget, then park. Unlike an expired JWT — which self-heals when the
 * session refreshes — an RLS denial is deterministic for the current claims,
 * so the elastic retry loop would hammer the server forever at the backoff
 * cap. The budget exists only to absorb the unprimed-socket race on the first
 * join. Recovery: markParked() tells the hook to re-subscribe on the next
 * TOKEN_REFRESHED instead of on a timer.
 *
 * @returns true when the channel was parked — the caller must NOT schedule a retry.
 */
function trackAuthorizationDenial(ctx: SubscribeContext, error?: Error): boolean {
  if (!isChannelAuthorizationDenial(error)) {
    // Denials must be consecutive: a mixed failure mode means the backend is
    // unstable, not that RLS rejects these claims.
    ctx.unauthorizedCountRef.current = 0;
    return false;
  }
  ctx.unauthorizedCountRef.current += 1;
  if (ctx.unauthorizedCountRef.current < MAX_UNAUTHORIZED_ATTEMPTS) {
    return false;
  }
  // Emit unconditionally — NOT gated on warnedRef. Parking is terminal for this
  // channel lifetime (removeChannel tombstones it), so there is no duplicate to
  // suppress. warnedRef is shared with the persistent-failure warning in
  // scheduleSubscribeRetry; gating on it here would swallow the parking warning
  // whenever the channel had already crossed the transient-failure threshold —
  // losing visibility into the exact Unauthorized-storm this warning exists for.
  captureChannelAuthorizationDenied(ctx.channelName);
  ctx.removeChannel(ctx.channel);
  ctx.markParked();
  return true;
}

export function handleSubscribeStatus(status: string, ctx: SubscribeContext, error?: Error): void {
  // Stale callback from a previous effect lifetime.
  if (ctx.generationRef.current !== ctx.generation) {
    ctx.removeChannel(ctx.channel);
    return;
  }

  // Ignore ANY late callback from a channel we've already torn down: its own
  // synchronous CLOSED echo (the SDK fires it inside unsubscribe(),
  // @supabase/realtime-js@2.99.3 RealtimeChannel.js:367-380), a late SUBSCRIBED,
  // or a trailing error. The channel lifetime is over even though the effect
  // generation is still current, so processing it would re-count retries,
  // schedule a phantom second retry, or fire onResubscribed for a dead channel.
  if (ctx.tearingDownChannelsRef.current.has(ctx.channel)) {
    return;
  }

  if (status === 'SUBSCRIBED') {
    ctx.retryCountRef.current = 0;
    ctx.channelFailureCountRef.current = 0;
    ctx.warnedRef.current = false;
    ctx.unauthorizedCountRef.current = 0;
    // The first SUBSCRIBED for a subscription is the initial join: covered by
    // the initial data load when that is answered after it, and by
    // onFirstSubscribed for a wrapper whose reads can be answered before it.
    // Every SUBSCRIBED after that means the socket dropped and rejoined:
    // reconcile whatever events were missed in the gap.
    if (ctx.hasSubscribedOnceRef.current) {
      ctx.fireOnResubscribed();
    } else {
      ctx.hasSubscribedOnceRef.current = true;
      ctx.fireOnFirstSubscribed();
    }
    return;
  }

  if (!RETRYABLE_SUBSCRIBE_STATUSES.has(status)) {
    return;
  }

  if (trackAuthorizationDenial(ctx, error)) {
    return;
  }

  // Transient error OR an expired-JWT auth error: tear down and retry with
  // backoff. Each retry re-primes auth from the current session (private
  // channels, via createAndSubscribe → getSession) or uses the realtime token
  // the TOKEN_REFRESHED handler refreshed (public channels), so an expired token
  // self-heals as soon as the session refreshes. Retrying (rather than parking
  // for the next TOKEN_REFRESHED event) is what makes recovery robust to
  // ordering: an auth error arriving JUST AFTER a refresh would otherwise wait
  // an entire token TTL for the next refresh. This is why the WS auth-expiry
  // error (RC-2: dead realtime after an overnight token expiry) recovers
  // instead of tearing down terminally. A genuinely-invalid JWT retries at the
  // capped backoff, which the one-shot Sentry warning surfaces on the normal
  // schedule; a lapsed one is held back until `EXPIRED_JWT_WARN_RETRIES`.
  ctx.removeChannel(ctx.channel);
  scheduleSubscribeRetry(ctx, status, error);
}
